import React, { useState, useEffect } from 'react';
import './App.css';
import RegisterPage from './Components/RegisterPage';
import ResetPasswordPage from './Components/ResetPasswordPage';
import '../src/assets/css/style.bundle.css';
import '../src/assets/bootstrap/css/bootstrap.min.css';
import '../src/assets/font/GoogleSans-Regular.ttf';
import '../src/assets/font/GoogleSans-Bold.ttf';
import '../src/assets/css/style.css';
import SignIn from './Components/SignIn';
import AuthRoute from './Components/AuthRoute';
import GuestRoute from './Components/GuestRoute';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ConfirmResetPassword from './Components/ConfirmResetPassword';
import HomePage from './Components/HomePage';
import HelpPage from './Components/HelpPage';
import Settings from './Components/Settings';
import Loader from './Components/Loader';
import History from './Components/History';
import ManageSubscription from './Components/ManageSubscription ';
import BillingHistory from './Components/BillingHistory ';
import Captions from './Components/Captions ';
import Images from './Components/Images';
import Avatars from './Components/Avatars';
import { useLocation } from 'react-router-dom';
import EmailVerifyMid from './Components/EmailVerifyMid';
import Sales from './Components/Sales';
import FlashSaleModal from './Components/FlashSaleModal';
import EmailVerify from './Components/EmailVerify';
import Emailconfirm from './Components/EmailConfirm';
import { GoogleOAuthProvider } from '@react-oauth/google';
import axios from 'axios';
import { LoadingProvider } from './Components/LoadinContext';
import NotFound from './Components/NotFound';
import Videos from './Components/Videos';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Initialize QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
    },
  },
});

// Function to fetch captions,avatars,images,videos (moved outside App for reusability)
const fetchCaptions = async () => {
  const authData = localStorage.getItem('auth');
  const token = authData ? JSON.parse(authData).token : null;
  const id = authData ? JSON.parse(authData).id : null;
  const baseurl = `${process.env.REACT_APP_BASE_URL}/plans`;

  if (!token || !id) {
    throw new Error("User authentication data is missing.");
  }

  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}/api/get-captions`,
    {
      params: { user_id: id, app_url: baseurl },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (response.data.status !== 200) {
    throw new Error('Error fetching captions');
  }

  return {
    captions: response.data.captions,
    info: response.data.info.text,
  };
};
const fetchAvatars = async () => {
  const authData = localStorage.getItem('auth');
  const token = authData ? JSON.parse(authData).token : null;
  const id = authData ? JSON.parse(authData).id : null;
  const baseurl = `${process.env.REACT_APP_BASE_URL}/plans`;

  if (!token || !id) {
    throw new Error("User authentication data is missing.");
  }

  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}/api/get-avatars`,
    {
      params: { user_id: id, app_url: baseurl },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (response.data.status !== 200) {
    throw new Error('Error fetching avatars');
  }

  return {
    avatars: response.data.avatars,
    info: response.data.info.text,
  };
};
const fetchImages = async () => {
  const authData = localStorage.getItem('auth');
  const token = authData ? JSON.parse(authData).token : null;
  const id = authData ? JSON.parse(authData).id : null;
  const baseurl = `${process.env.REACT_APP_BASE_URL}/plans`;

  if (!token || !id) {
    throw new Error("User authentication data is missing.");
  }

  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}/api/get-images`,
    {
      params: { user_id: id, app_url: baseurl },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (response.data.status !== 200) {
    throw new Error('Error fetching images');
  }

  return {
    images: response.data.images,
    info: response.data.info.text,
  };
};
const fetchVideos = async () => {
  const authData = localStorage.getItem('auth');
  const token = authData ? JSON.parse(authData).token : null;
  const id = authData ? JSON.parse(authData).id : null;
  const baseurl = `${process.env.REACT_APP_BASE_URL}/plans`;

  if (!token || !id) {
    throw new Error("User authentication data is missing.");
  }

  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}/api/get-videos`,
    {
      params: { user_id: id, app_url: baseurl },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (response.data.status !== 200) {
    throw new Error('Error fetching videos');
  }

  return {
    videos: response.data.videos,
    info: response.data.info.text,
  };
};
const fetchSubscriptionData = async () => {
  const authData = localStorage.getItem('auth');
  if (!authData) throw new Error("User not authenticated");

  let parsedAuth;
  try {
      parsedAuth = JSON.parse(authData);
  } catch (error) {
      throw new Error("Invalid authentication data");
  }

  const { token, id } = parsedAuth;
  const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/plans`,
      {
          params: { user_id: id, guest: false },
          headers: {
              Authorization: `Bearer ${token}`,
          },
      }
  );

  if (response.data.status === 200) {
      return response.data;
  } else if (response.data.status === 401) {
      throw new Error("Unauthorized");
  } else {
      throw new Error(response.data.message || "Failed to fetch data");
  }
};
// ScrollToTop component
function ScrollToTop({ isModalOpen }) {
  const location = useLocation();

  useEffect(() => {
    if (!isModalOpen && location.pathname !== "/sales") {
      window.scrollTo(0, 0);
    }
  }, [location, isModalOpen]);

  return null;
}

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [googleClientId, setGoogleClientId] = useState(() => localStorage.getItem('Googleclientid') || null);
  const [isModalOpen, setModalOpen] = useState(false);

  // Initial loading effect
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  // Flash sale modal effect
  useEffect(() => {
    const timer = setTimeout(() => setModalOpen(true), 6000);
    return () => clearTimeout(timer);
  }, []);

  // Fetch Google Client ID
  useEffect(() => {
    const apiget = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/socialite/credentials`,
        );
        if (response.data.status === 200) {
          const clientId = response.data.google.client_id;
          localStorage.setItem('Googleclientid', clientId);
          setGoogleClientId(clientId);
        } else {
          console.log("API call was successful, but status is not 200.");
        }
      } catch (err) {
        console.error("Error:", err);
      }
    };
    apiget();
  }, []);


  // Inside App.js useEffect for prefetching
  useEffect(() => {
    const authData = localStorage.getItem('auth');
    if (authData) {
      // Prefetch captions
      queryClient.prefetchQuery({
        queryKey: ['captions'],
        queryFn: fetchCaptions,
        staleTime: Infinity,
        cacheTime: Infinity,
      }).catch((error) => {
        console.error("Prefetching captions failed:", error);
      });

      // Prefetch avatars
      queryClient.prefetchQuery({
        queryKey: ['avatars'],
        queryFn: fetchAvatars,
        staleTime: Infinity,
        cacheTime: Infinity,
      }).catch((error) => {
        console.error("Prefetching avatars failed:", error);
      });

      // Prefetch images
      queryClient.prefetchQuery({
        queryKey: ['images'],
        queryFn: fetchImages,
        staleTime: Infinity,
        cacheTime: Infinity,
      }).catch((error) => {
        console.error("Prefetching images failed:", error);
      });

      // Prefetch videos
      queryClient.prefetchQuery({
        queryKey: ['videos'],
        queryFn: fetchVideos,
        staleTime: Infinity,
        cacheTime: Infinity,
      }).catch((error) => {
        console.error("Prefetching videos failed:", error);
      });

      // Prefetch subscription data
      queryClient.prefetchQuery({
        queryKey: ['subscriptionData'],
        queryFn: fetchSubscriptionData,
        staleTime: Infinity,
        cacheTime: Infinity,
      }).catch((error) => {
        console.error("Prefetching subscription data failed:", error);
      });
    }
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <LoadingProvider>
        <Router>
          <ScrollToTop isModalOpen={isModalOpen} />
          {isLoading && <Loader />}
          <GoogleOAuthProvider clientId={googleClientId}>
            <Routes>
              <Route path="*" element={<NotFound />} />
              <Route
                path="/history"
                element={
                  <AuthRoute>
                    <EmailVerifyMid>
                      <History />
                    </EmailVerifyMid>
                  </AuthRoute>
                }
              />
              <Route
                path="/email/verify/confirm/:id1/:id2"
                element={
                  <AuthRoute>
                    <Emailconfirm />
                  </AuthRoute>
                }
              />
              <Route
                path="/email/verify"
                element={
                  <AuthRoute>
                    <EmailVerifyMid>
                      <EmailVerify />
                    </EmailVerifyMid>
                  </AuthRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <AuthRoute>
                    <EmailVerifyMid>
                      <BillingHistory />
                    </EmailVerifyMid>
                  </AuthRoute>
                }
              />
              <Route
                path="/avatars"
                element={
                  <AuthRoute>
                    <EmailVerifyMid>
                      <Avatars />
                    </EmailVerifyMid>
                  </AuthRoute>
                }
              />
              <Route
                path="/videos"
                element={
                  <AuthRoute>
                    <EmailVerifyMid>
                      <Videos />
                    </EmailVerifyMid>
                  </AuthRoute>
                }
              />
              <Route
                path="/sales"
                element={
                  <>
                    <Sales />
                    <FlashSaleModal isModalVisible={isModalOpen} setModalVisible={setModalOpen} />
                  </>
                }
              />
              <Route
                path="/captions"
                element={
                  <AuthRoute>
                    <EmailVerifyMid>
                      <Captions />
                    </EmailVerifyMid>
                  </AuthRoute>
                }
              />
              <Route
                path="/images"
                element={
                  <AuthRoute>
                    <EmailVerifyMid>
                      <Images />
                    </EmailVerifyMid>
                  </AuthRoute>
                }
              />
              <Route
                path="/plans"
                element={
                  <AuthRoute>
                    <EmailVerifyMid>
                      <ManageSubscription />
                    </EmailVerifyMid>
                  </AuthRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <AuthRoute>
                    <EmailVerifyMid>
                      <Settings />
                    </EmailVerifyMid>
                  </AuthRoute>
                }
              />
              <Route
                path="/help"
                element={
                  <AuthRoute>
                    <EmailVerifyMid>
                      <HelpPage />
                    </EmailVerifyMid>
                  </AuthRoute>
                }
              />
              <Route
                path="/"
                element={
                  <AuthRoute>
                    <EmailVerifyMid>
                      <HomePage />
                    </EmailVerifyMid>
                  </AuthRoute>
                }
              />
              {/* Guest Routes */}
              <Route
                path="/register"
                element={
                  <GuestRoute>
                    <RegisterPage />
                  </GuestRoute>
                }
              />
              <Route
                path="/login"
                element={
                  <GuestRoute>
                    <SignIn />
                  </GuestRoute>
                }
              />
              <Route
                path="/resetpassword"
                element={
                  <GuestRoute>
                    <ResetPasswordPage />
                  </GuestRoute>
                }
              />
              <Route
                path="/confirmresetpassword"
                element={
                  <GuestRoute>
                    <ConfirmResetPassword />
                  </GuestRoute>
                }
              />
            </Routes>
          </GoogleOAuthProvider>
        </Router>
      </LoadingProvider>
    </QueryClientProvider>
  );
}

export default App;